import * as React from 'react';
import { useContext } from 'react';
import { useTheme } from '../../../../theme';
import cn from 'classnames';

import { SeatComfort, SeatMapRestrictions } from '@websky/graphql';
import { PassengerState } from '../../../../Checkout/store/passengers/reducers';
import { RowWithParts, Seat as SeatType, ServicesMap } from '../../../types';
import { GdsServiceById } from '@shared/lib/gdsService';

import ExitRow from '../ExitRow/ExitRow';
import Parts from './Parts/Parts';
import { ModeContext } from '../../../../context';
import { Mode } from '../../../../types';
import RowContextProvider from './context';
import { getEdgeSeatsNumbers } from './utils';

export interface Props {
	passengers: PassengerState[];
	allSeatServices: ServicesMap;
	services: ServicesMap;
	gdsServiceById: GdsServiceById;
	row: RowWithParts;
	nextRow: RowWithParts;
	rowIndex: number;
	lastRow: number;
	onSelect: (seat: SeatType[], passengerId: string) => void;
	onClear?: () => void;
	passengerId: string;
	segmentId: string;
	isSeatMapWide: boolean;
	newClassType: boolean;
	activeDoubleSeats?: boolean;
	rowsWithDifferentClasses: Map<number, SeatComfort>;
	rows: RowWithParts[];
	activeFilters: SeatMapRestrictions[];
}

const Row: React.FunctionComponent<Props> = React.memo(({ row, ...props }) => {
	const theme = useTheme('SeatMap').SeatMapStyles;
	const isLastRow = props.lastRow === props.rowIndex;
	const isCheckinMode = useContext(ModeContext) === Mode.Checkin;

	const edgeSeatsNumbers = getEdgeSeatsNumbers(row);

	const isSamePartsType = React.useMemo(() => {
		const firstClass = row.parts.find(part => part.service.seatService?.comfort);

		return !row.parts.find(
			part =>
				!part.service.isAisle &&
				part.service.seatService &&
				part.service.seatService?.comfort !== firstClass?.service?.seatService?.comfort
		);
	}, [row]);

	return (
		<RowContextProvider value={{ row }}>
			{!isLastRow && row.exitRow && <ExitRow />}
			<div
				className={cn(theme.row, {
					[theme.row_otherClass]: props.newClassType,
					[theme.row_nextRowHasOtherClass]: props.rowsWithDifferentClasses.has(props.nextRow?.number),
					[theme.row_last]: props.rowIndex === props.lastRow,
					[theme.row_withExit]: row.exitRow
				})}
				with-wings={row.wing ? 'true' : 'false'}
			>
				{row &&
					row.parts &&
					row.parts.map((part, partIndex) => {
						const nextPart = row.parts[partIndex + 1];
						const currentPartService = part?.service?.seatService?.comfort;

						return (
							<Parts
								{...props}
								key={partIndex}
								partIndex={partIndex}
								nextPartService={nextPart?.service?.seatService}
								prevPartService={partIndex > 0 && row.parts[partIndex - 1]?.service?.seatService}
								part={part}
								isCheckinMode={isCheckinMode}
								partService={currentPartService}
								serviceId={currentPartService}
								newClass={props.newClassType}
								onSelect={props.onSelect}
								onClear={props.onClear}
								isStatic={isSamePartsType}
								rows={props.rows}
								rowIndex={props.rowIndex}
								rowsWithDifferentClasses={props.rowsWithDifferentClasses}
								edgeSeatsNumbers={edgeSeatsNumbers}
							/>
						);
					})}
			</div>

			{isLastRow && row.exitRow && <ExitRow />}
		</RowContextProvider>
	);
});

export default Row;
